import { Chip, styled, useTheme } from "@mui/material";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as ProgressIcon } from "Components/_Shared/Icons/Assets/progress.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

const StyledChip = styled(Chip, {
  shouldForwardProp: isPropValid,
})<{ $color: string; $bgColor: string; $fontWeight: number }>`
  color: ${(props) => props.$color};
  border-color: ${(props) => props.$color};
  border: ${(props) => (props.variant === "outlined" ? "1px solid" : "0")}; 
  background: ${(props) => (props.variant === "outlined" ? "transparent" : props.$bgColor)}; 
  padding: ${(props) => props.theme.spacing(0, Spacing.S)};
  font-weight: ${(props) => props.$fontWeight};
`;

type Props = {
  readonly state: AnalysisDetailState;
  readonly variant?: "filled" | "outlined";
  readonly size?: "small" | "medium";
  readonly fontWeight?: number;
};

export const AnalysisStatusChip: React.FC<Props> = ({ state, variant = "outlined", size = "medium", fontWeight = 400 }) => {
  const theme = useTheme();
  const { t } = useResource();

  const iconSize = size === "small" ? 14 : 20;
  const colorMap = {
    [AnalysisDetailState.in_progress]: theme.palette.info.main,
    [AnalysisDetailState.completed]: theme.palette.success.main,
    [AnalysisDetailState.declined]: theme.palette.warning.dark,
  };

  const bgColorMap = {
    [AnalysisDetailState.in_progress]: theme.palette.info.light,
    [AnalysisDetailState.completed]: theme.palette.success.light,
    [AnalysisDetailState.declined]: theme.palette.warning.light,
  };

  const getText = (state: AnalysisDetailState): string => {
    switch (state) {
      case AnalysisDetailState.in_progress:
        return t(Resources.AnalysesListing.Status.InProgress);
      case AnalysisDetailState.completed:
        return t(Resources.AnalysesListing.Status.Completed);
      case AnalysisDetailState.declined:
        return t(Resources.AnalysesListing.Status.Declined);
    }
  };

  const getIcon = (state: AnalysisDetailState): React.ReactElement => {
    switch (state) {
      case AnalysisDetailState.in_progress:
        return <ProgressIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
      case AnalysisDetailState.completed:
        return <CheckIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
      case AnalysisDetailState.declined:
        return <UnavailableIcon color={colorMap[state]} width={iconSize} height={iconSize} />;
    }
  };

  return (
    <StyledChip
      size={size}
      $color={colorMap[state]}
      $bgColor={bgColorMap[state]}
      $fontWeight={fontWeight}
      variant={variant}
      icon={getIcon(state)}
      label={getText(state)}
    />
  );
};
